import React from "react";
import { Mail, MapPin, Phone } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LOGOS, NAV_LINKS, PRODUCTS } from "../../lib/constants";

const LEGAL_LINKS = [
  { label: "Privacy Policy", to: "/privacy-policy" },
  { label: "Terms & Conditions", to: "/terms" },
  { label: "Refund Policy", to: "/refund-policy" },
];

export const Footer = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const handleNav = (href) => {
    if (location.pathname !== "/") {
      navigate("/" + href);
      return;
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <footer
      data-testid="site-footer"
      className="relative bg-[#1A1A2E] text-white pt-16 pb-10 overflow-hidden"
    >
      <div className="absolute inset-0 bg-grid-dark opacity-40 pointer-events-none" />
      <div className="orb w-[420px] h-[420px] bg-[#7B5EA7] -top-40 -right-20" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-8 pb-12 border-b border-white/10">
          {/* Brand */}
          <div className="col-span-2 lg:col-span-4">
            <div className="flex items-center gap-3 mb-4">
              <img
                src={LOGOS.main}
                alt="Ping"
                className="h-12 w-12 object-contain bg-white rounded-xl p-1 shadow-lg shadow-[#7B5EA7]/10"
              />
              <span className="text-4xl font-black tracking-tight ping-gradient-text">
                Ping
              </span>
            </div>
            <p className="text-white/65 text-[15px] leading-relaxed max-w-sm">
              WhatsApp notifications for UAE service businesses. Stop the calls,
              build your customer database, grow with seasonal campaigns.
            </p>

            <div className="mt-6 space-y-2.5 text-sm">
              <a
                href="mailto:tariq@pingme.ae"
                data-testid="footer-email"
                className="flex items-center gap-2.5 text-white/75 hover:text-white transition-colors group"
              >
                <Mail size={15} className="text-[#4B9FE8] group-hover:text-[#7B5EA7] transition-colors" />
                tariq@pingme.ae
              </a>
              <a
                href="tel:+971581912500"
                data-testid="footer-phone"
                className="flex items-center gap-2.5 text-white/75 hover:text-white transition-colors group"
              >
                <Phone size={15} className="text-[#4B9FE8] group-hover:text-[#7B5EA7] transition-colors" />
                +971 58 191 2500
              </a>
              <div className="flex items-start gap-2.5 text-white/75">
                <MapPin size={15} className="text-[#4B9FE8] mt-0.5 flex-shrink-0" />
                <span>
                  Pingme Software Design · Registered in Dubai, United Arab
                  Emirates
                </span>
              </div>
            </div>
          </div>

          {/* Navigate */}
          <div className="col-span-1 lg:col-span-2">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-white/50 mb-4">
              Navigate
            </div>
            <ul className="space-y-3" data-testid="footer-nav-links">
              {NAV_LINKS.map((link) => (
                <li key={link.href}>
                  <button
                    onClick={() => handleNav(link.href)}
                    className="text-white/75 hover:text-white text-[15px] transition-colors"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Products */}
          <div className="col-span-1 lg:col-span-3">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-white/50 mb-4">
              Products
            </div>
            <ul className="space-y-3" data-testid="footer-products">
              {PRODUCTS.map((p) => (
                <li key={p.id}>
                  <button
                    onClick={() => handleNav("#products")}
                    className="flex items-center gap-2.5 text-white/75 hover:text-white text-[15px] transition-colors"
                  >
                    <span className="ping-gradient-text font-semibold">
                      {p.name}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div className="col-span-2 lg:col-span-3">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-white/50 mb-4">
              Legal
            </div>
            <ul className="space-y-3" data-testid="footer-legal">
              {LEGAL_LINKS.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    data-testid={`footer-legal-${link.to.replace("/", "")}`}
                    className="text-white/75 hover:text-white text-[15px] transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="pt-7 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-white/55">
          <p data-testid="footer-copyright">
            © 2026 Pingme Software Design. All rights reserved.
          </p>
          <p>WhatsApp Business notification platform · www.pingme.ae</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
